import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { $api } from '../api/queryClient'
import { useUsers } from '../api/queries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useDebounce } from '../hooks/useDebounce'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { TooltipProvider } from './ui/tooltip'
import { AppIcon } from './ui/icon'
import type { EntityLog } from '../api/schema'
import { ActivityEntry } from './activity/ActivityEntry'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { mdiBookmarkCheckOutline, mdiLoading, mdiRefresh } from '@mdi/js'

const PAGE_SIZE = 50

// Same entity-table option list as the per-asset feed's ENTITY_FILTERS, plus
// "all". Values map 1:1 to the backend entity_table filter.
const ENTITY_FILTER_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: 'all', labelKey: 'activity.filter_all' },
  { value: 'assets', labelKey: 'activity.filter_asset' },
  { value: 'asset_data', labelKey: 'activity.filter_properties' },
  { value: 'documents', labelKey: 'activity.filter_documents' },
  { value: 'contract_assets', labelKey: 'activity.filter_contracts' },
  { value: 'pages', labelKey: 'activity.filter_notes' },
  { value: 'tags_entities', labelKey: 'activity.filter_tags' },
  { value: 'issues', labelKey: 'activity.filter_issues' },
  { value: 'asset_frame_registrations', labelKey: 'activity.filter_plans' },
]

const OPERATION_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: 'all', labelKey: 'activity.filter_all' },
  { value: 'INSERT', labelKey: 'activity.operation_insert' },
  { value: 'UPDATE', labelKey: 'activity.operation_update' },
  { value: 'DELETE', labelKey: 'activity.operation_delete' },
  { value: 'RESTORE', labelKey: 'activity.operation_restore' },
]

const ALL = 'all'
const SYSTEM = 'system'

export function GlobalActivityLog() {
  const { t, i18n } = useTranslation('assets')
  const navigate = useNavigate()
  const params = useParams({ from: '/$org/activity' })
  const search = useSearch({ from: '/$org/activity' })
  const org = params.org
  const locale = i18n.language || 'en'

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('activity.page_title'), path: `/${org}/activity` },
  ])

  const { data: users } = useUsers(org, { include: ['picture'] })

  // Derived filter values from the URL (source of truth).
  const entityTable = search.entity_table || ALL
  const operation = search.operation || ALL
  const userUid = search.user_uid || ALL
  const from = search.from || ''
  const to = search.to || ''
  // Resume marker (not a filter): the created_at of the entry the feed is
  // pinned to. Set by an entry's bookmark button, cleared from the banner.
  const cursor = search.cursor || ''
  // The URL-committed search term. The input itself is debounced (see
  // qInput/debouncedQ below) so typing doesn't spam the URL/API on every
  // keystroke; `q` is what's actually applied.
  const q = search.q || ''

  const updateFilters = useCallback(
    (patch: Partial<{ from: string; to: string; entity_table: string; user_uid: string; operation: string; q: string }>) => {
      const merged = {
        from: search.from,
        to: search.to,
        entity_table: search.entity_table,
        user_uid: search.user_uid,
        operation: search.operation,
        q: search.q,
        ...patch,
      }
      // Drop empty / "all" values so the URL stays clean.
      const clean = (v: string | undefined): string | undefined => (!v || v === ALL ? undefined : v)
      navigate({
        to: '/$org/activity',
        params: { org },
        search: {
          from: clean(merged.from),
          to: clean(merged.to),
          entity_table: clean(merged.entity_table),
          user_uid: clean(merged.user_uid),
          operation: clean(merged.operation),
          q: clean(merged.q),
          // Changing a filter re-shapes the feed, so a position pinned in the
          // old one is meaningless — drop it.
          cursor: undefined,
        },
        replace: true,
      })
    },
    [navigate, org, search],
  )

  // The search input's own fast-updating state, debounced 300ms before it
  // reaches the URL/API (spec 2026-09-25-03: "dynamic" = filter-as-you-type).
  // Kept in sync with the URL so back/forward navigation and the "clear"
  // affordance below both update the visible text.
  const [qInput, setQInput] = useState(q)
  const debouncedQ = useDebounce(qInput, 300)

  useEffect(() => {
    setQInput(q)
  }, [q])

  useEffect(() => {
    if (debouncedQ !== q) {
      updateFilters({ q: debouncedQ })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const clearCursor = useCallback(() => {
    navigate({
      to: '/$org/activity',
      params: { org },
      search: prev => ({ ...prev, cursor: undefined }),
      replace: false,
    })
  }, [navigate, org])

  const [offset, setOffset] = useState(0)
  const [accumulated, setAccumulated] = useState<EntityLog[]>([])

  // Reset pagination whenever a filter — or the resume marker — changes.
  useEffect(() => {
    setOffset(0)
    setAccumulated([])
  }, [from, to, entityTable, operation, userUid, cursor, q])

  const query: Record<string, unknown> = { limit: PAGE_SIZE, offset }
  if (from) query.from = from
  if (to) query.to = to
  if (entityTable !== ALL) query.entity_table = entityTable
  if (operation !== ALL) query.operation = operation
  if (userUid !== ALL) query.user_uid = userUid
  if (cursor) query.cursor = cursor
  if (q) query.q = q

  const { data, isLoading, isFetching, refetch } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/logs' as any,
    {
      params: {
        path: { orgCode: org },
        query,
      },
    },
  ) as {
    data: any
    isLoading: boolean
    isFetching: boolean
    refetch: () => Promise<{ data?: { items?: EntityLog[] } }>
  }

  const items: EntityLog[] = (data?.items as EntityLog[]) || []
  const total: number = (data?.total as number) || 0

  useEffect(() => {
    if (!data) return
    if (offset === 0) {
      setAccumulated(items)
    } else {
      setAccumulated(prev => {
        const seen = new Set(prev.map(i => i.uid))
        const newOnes = items.filter(i => !seen.has(i.uid))
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const displayItems = accumulated
  const hasMore = displayItems.length < total

  const handleRefresh = async () => {
    setOffset(0)
    const res = await refetch()
    if (res?.data?.items) {
      setAccumulated(res.data.items)
    }
  }

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!hasMore) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry?.isIntersecting && !isFetching && hasMore) {
          setOffset(displayItems.length)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, isFetching, displayItems.length])

  return (
    <div className="container mx-auto max-w-5xl">
      <PageHeader
        className="mb-6"
        icon={SECTION_ICONS.activity}
        title={t('activity.page_title')}
      />

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="activity-filter-bar">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="activity-filter-q">{t('activity.filter_search')}</label>
            <Input
              id="activity-filter-q"
              type="search"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder={t('activity.filter_search_placeholder')}
              className="w-[220px]"
              data-testid="activity-filter-q"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="activity-from">{t('activity.filter_date_from')}</label>
            <Input
              id="activity-from"
              type="date"
              value={from}
              onChange={e => updateFilters({ from: e.target.value })}
              className="w-[160px]"
              data-testid="activity-filter-from"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="activity-to">{t('activity.filter_date_to')}</label>
            <Input
              id="activity-to"
              type="date"
              value={to}
              onChange={e => updateFilters({ to: e.target.value })}
              className="w-[160px]"
              data-testid="activity-filter-to"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">{t('activity.filter_entity')}</label>
            <Select value={entityTable} onValueChange={v => updateFilters({ entity_table: v })}>
              <SelectTrigger className="w-[170px]" data-testid="activity-filter-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_FILTER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`activity-entity-${opt.value}`}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">{t('activity.filter_user')}</label>
            <Select value={userUid} onValueChange={v => updateFilters({ user_uid: v })}>
              <SelectTrigger className="w-[180px]" data-testid="activity-filter-user">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="activity-user-all">{t('activity.filter_all')}</SelectItem>
                <SelectItem value={SYSTEM} data-testid="activity-user-system">{t('activity.system_user')}</SelectItem>
                {(users || []).map(u => (
                  <SelectItem key={u.uid} value={u.uid} data-testid={`activity-user-${u.uid}`}>
                    {u.name || u.email || u.uid}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">{t('activity.filter_operation')}</label>
            <Select value={operation} onValueChange={v => updateFilters({ operation: v })}>
              <SelectTrigger className="w-[150px]" data-testid="activity-filter-operation">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPERATION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`activity-operation-${opt.value}`}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isFetching}
            data-testid="activity-refresh"
            aria-label={t('activity.refresh')}
            title={t('activity.refresh')}
          >
            <AppIcon path={mdiRefresh} size="sm" className={`${isFetching ? 'motion-safe:animate-spin' : ''}`} />
          </Button>
        </div>

        {cursor && (
          <div
            className="flex items-center gap-2 flex-wrap mb-4 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
            data-testid="activity-cursor-banner"
          >
            <AppIcon path={mdiBookmarkCheckOutline} size="sm" className="shrink-0 text-primary" />
            <span className="text-foreground">
              {t('activity.marker_resumed_from', { date: new Date(cursor).toLocaleString(locale) })}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={clearCursor}
              data-testid="activity-cursor-clear"
            >
              {t('activity.marker_back_to_latest')}
            </Button>
          </div>
        )}

        {isLoading && displayItems.length === 0 && (
          <div className="flex justify-center py-8">
            <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
          </div>
        )}

        {!isLoading && displayItems.length === 0 && (
          <EmptyState
            variant="inline"
            className="py-12"
            icon={SECTION_ICONS.activity}
            title={q ? t('activity.empty_search') : t('activity.empty')}
            data-testid="activity-empty"
            live
          />
        )}

        {displayItems.length > 0 && (
          <TooltipProvider>
            <ul className="space-y-4">
              {displayItems.map(entry => (
                <ActivityEntry
                  key={entry.uid}
                  entry={entry}
                  org={org}
                  locale={locale}
                  linkEntity
                  showMarker
                  isMarked={Boolean(cursor) && entry.created_at === cursor}
                />
              ))}
            </ul>
          </TooltipProvider>
        )}

        {displayItems.length > 0 && (
          <div className="flex justify-center mt-4">
            {hasMore ? (
              <div
                ref={sentinelRef}
                className="flex items-center gap-2 py-2 text-xs text-muted-foreground"
                data-testid="activity-load-more"
              >
                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                {t('activity.load_more')}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground" data-testid="activity-no-more">
                {t('activity.no_more')}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
